import React from 'react';

import { Button, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { PlaceholderSocialCard } from '../PlaceholderSocialCard';

/**
 * Empty state component shown when no recommendation groups are available.
 * Displays a message prompting users to edit their petition text to find groups,
 * along with two social cards (Facebook and Reddit) as visual examples.
 *
 * Layout:
 * - Desktop: Two-column layout (content, social cards)
 * - Mobile: Single column, stacked layout
 *
 * ```tsx
 * <EmptyState />
 * ```
 */
export const EmptyState: React.FC<EmptyProps> = (): React.JSX.Element => {
	const {
		data: {
			petition: { slug },
		},
	} = useStarterDashboardV2PageData();

	const navigate = useNavigate();
	const editPetitionUrl = `/p/${encodeURIComponent(slug)}/edit`;

	const handleEditPetitionClick = () => {
		navigate(editPetitionUrl);
	};

	return (
		<section
			aria-labelledby="recommendations-empty-state-title"
			className="bg-background-base px-4 py-8 md:p-12"
			data-testid="recommendations-empty-state"
		>
			<div className="flex flex-col gap-8 md:flex-row">
				<div className="flex flex-1 flex-col gap-5 md:max-w-[620px]">
					<div className="flex flex-col gap-3">
						<Text
							id="recommendations-empty-state-title"
							variant="heading"
							className="text-size-heading-xs md:text-size-heading-small"
						>
							<Translate value="corgi.petition.starter_dashboard_v2.recommendations.empty_state.title_groups" />
						</Text>
						<Text variant="body" className="text-typography-medium">
							<Translate value="corgi.petition.starter_dashboard_v2.recommendations.empty_state.description_no_groups" />
						</Text>
					</div>
					<div className="w-full md:w-auto">
						<Button
							variant="secondary"
							size="small"
							onClick={handleEditPetitionClick}
							data-testid="edit-petition-button"
							data-qa="edit-petition-button"
							className="mt-1 h-[40px] w-full md:w-auto"
						>
							<Translate value="corgi.petition.starter_dashboard_v2.recommendations.empty_state.edit_petition_button" />
						</Button>
					</div>
				</div>

				<div className="flex flex-row gap-4 sm:justify-center md:flex-shrink-0">
					<PlaceholderSocialCard platform="reddit" isReskin />
					<PlaceholderSocialCard platform="facebook" isReskin />
				</div>
			</div>
		</section>
	);
};
